import React, { useState, useEffect, useCallback } from 'react';
import { Zap, Cloud, RefreshCw, AlertTriangle, Globe } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

/**
 * Toggle Global unifié - Bascule Texte + Images ensemble
 * Usage: <GlobalModeToggle />
 * Visible uniquement pour les amiraux
 */

const GlobalModeToggle = ({ className = '' }) => {
  const [globalMode, setGlobalMode] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userGrade, setUserGrade] = useState(null);
  
  // États pour le dialogue de confirmation
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingMode, setPendingMode] = useState(null);

  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  const fetchGlobalMode = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${API_BASE}/api/admin/global-mode`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setGlobalMode(data.global_mode);
      }
    } catch (err) {
      console.error('Erreur chargement mode global:', err);
    } finally {
      setIsLoading(false);
    }
  }, [API_BASE]);

  useEffect(() => {
    const checkGrade = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          setIsLoading(false);
          return;
        }
        
        const response = await fetch(`${API_BASE}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const user = await response.json();
          setUserGrade(user.grade);
          
          if (user.grade === 'amiral') {
            fetchGlobalMode();
          } else {
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
      } catch (err) {
        console.error('[GlobalModeToggle] Erreur:', err);
        setIsLoading(false);
      }
    };
    
    checkGrade();
  }, [API_BASE, fetchGlobalMode]);

  const handleToggleClick = () => {
    const newMode = globalMode === 'emergent' ? 'direct' : 'emergent';
    setPendingMode(newMode);
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    if (!pendingMode) return;
    
    setShowConfirm(false);
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${API_BASE}/api/admin/global-mode`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ mode: pendingMode })
      });
      
      if (response.ok) {
        const data = await response.json();
        setGlobalMode(data.global_mode);
      }
    } catch (err) {
      console.error('Erreur changement mode global:', err);
    } finally {
      setIsLoading(false);
      setPendingMode(null);
    }
  };

  const handleCancel = () => {
    setShowConfirm(false);
    setPendingMode(null);
  };

  // Attendre de savoir si l'utilisateur est amiral
  if (isLoading && userGrade === null) {
    return null;
  }
  
  // Ne rien afficher si l'utilisateur n'est pas amiral
  if (userGrade !== 'amiral') {
    return null;
  }

  const isEmergent = globalMode === 'emergent';

  return (
    <>
      <div 
        className={`inline-flex items-center gap-2 px-3 py-2 bg-slate-800/80 rounded-lg border border-slate-600 ${className}`}
        data-testid="global-mode-toggle-container"
      >
        <Globe className="w-4 h-4 text-slate-400" />
        <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">Global</span>
        <button
          onClick={handleToggleClick}
          disabled={isLoading}
          data-testid="global-mode-toggle-button"
          className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-bold transition-all border ${
            isEmergent 
              ? 'bg-purple-600 text-white border-purple-400 hover:bg-purple-500' 
              : 'bg-blue-600 text-white border-blue-400 hover:bg-blue-500'
          } ${isLoading ? 'opacity-50 cursor-wait' : 'cursor-pointer hover:scale-105'}`}
          title={`Mode global: ${globalMode}. Cliquer pour basculer Texte + Images.`}
        >
          {isLoading ? (
            <RefreshCw className="w-3 h-3 animate-spin" />
          ) : isEmergent ? (
            <Zap className="w-3 h-3" />
          ) : (
            <Cloud className="w-3 h-3" />
          )}
          <span>{isEmergent ? 'Emergent' : 'Direct'}</span>
        </button>
      </div>

      {/* Popup de confirmation */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent className="bg-slate-900 border-slate-600">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-slate-200">
              <Globe className="w-5 h-5 text-blue-400" />
              Changer le mode Global (Texte + Images)
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300 space-y-3">
              <p>
                Ce switch change <span className="font-bold text-white">simultanément</span> le mode 
                pour la génération de texte ET d'images.
              </p>
              <div className="flex items-center justify-center gap-4 py-3">
                <div className={`px-3 py-2 rounded-lg border ${
                  isEmergent 
                    ? 'bg-purple-500/20 border-purple-500 text-purple-300'
                    : 'bg-blue-500/20 border-blue-500 text-blue-300'
                }`}>
                  <div className="flex items-center gap-2">
                    {isEmergent ? <Zap className="w-4 h-4" /> : <Cloud className="w-4 h-4" />}
                    <span className="font-medium text-sm">{isEmergent ? 'Emergent' : 'Direct'}</span>
                  </div>
                  <span className="text-xs opacity-70">(actuel)</span>
                </div>
                <span className="text-slate-500">→</span>
                <div className={`px-3 py-2 rounded-lg border ${
                  pendingMode === 'emergent'
                    ? 'bg-purple-500/20 border-purple-500 text-purple-300'
                    : 'bg-blue-500/20 border-blue-500 text-blue-300'
                }`}>
                  <div className="flex items-center gap-2">
                    {pendingMode === 'emergent' ? <Zap className="w-4 h-4" /> : <Cloud className="w-4 h-4" />}
                    <span className="font-medium text-sm">{pendingMode === 'emergent' ? 'Emergent' : 'Direct'}</span>
                  </div>
                  <span className="text-xs opacity-70">(nouveau)</span>
                </div>
              </div>
              <div className="text-sm space-y-1 bg-slate-800 p-3 rounded-lg">
                <p><span className="text-slate-400">Texte:</span> {pendingMode === 'emergent' ? 'Claude (Emergent)' : 'Claude (Anthropic)'}</p>
                <p><span className="text-slate-400">Images:</span> {pendingMode === 'emergent' ? 'Gemini (Emergent)' : 'OpenAI gpt-image-1.5'}</p>
              </div>
              <p className="text-amber-400 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                {pendingMode === 'direct'
                  ? "Le mode Direct utilise vos clés API personnelles."
                  : "Le mode Emergent utilise la clé Universal Key (vérifiez votre solde)."}
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={handleCancel}
              className="bg-slate-700 hover:bg-slate-600 border-slate-600 text-slate-200"
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirm}
              className={`font-bold text-white ${
                pendingMode === 'emergent'
                  ? 'bg-purple-600 hover:bg-purple-500 border-purple-500'
                  : 'bg-blue-600 hover:bg-blue-500 border-blue-500'
              }`}
            >
              Confirmer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default GlobalModeToggle;
